import { Icon } from '@chakra-ui/react';
import { HStack, IconButtonWithTooltip, Td, Text } from '@elkaso-app/web-design';
import type { DealCategoryTreeNode } from 'apis/exclusive-deals';
import { useMemo } from 'react';
import { FiEdit } from 'react-icons/fi';
import { findCategoryPath } from './category-tree-helpers';
import type { CategoryRef, ProductOption } from './editable-deal-row.types';
import { GridCategoryModal } from './grid-category-modal';

interface Props {
  product: ProductOption | null;
  /** Full category forest (shared fetch — React Query dedupes across cells). */
  tree: DealCategoryTreeNode[];
  isInvalid?: boolean;
  onSelect: (category: CategoryRef) => void;
}

/**
 * Category column cell: shows the product's full category breadcrumb (root › … › leaf)
 * and lets the user re-pick it via the cascade modal. The pick only patches the row's
 * product — persistence rides the bulk-upsert Save, which applies catalogCategoryId
 * to the product on both create and update paths.
 */
export const CategoryCell = ({ product, tree, isInvalid, onSelect }: Props) => {
  const categoryId = product?.catalogCategoryId ?? null;

  const breadcrumb = useMemo(() => {
    if (categoryId == null) return '';
    const path = findCategoryPath(tree, categoryId);
    if (path.length) return path.map((n) => n.title || `Untitled (${n.id})`).join(' › ');
    // Tree still loading (or id absent from it) — fall back to the leaf title we carry.
    return product?.categoryTitle || `#${categoryId}`;
  }, [tree, categoryId, product?.categoryTitle]);

  if (!product) {
    return (
      <Td>
        <Text fontSize='xs' color='gray.600'>
          -
        </Text>
      </Td>
    );
  }

  return (
    <Td>
      <HStack spacing='1' align='center' minW='200px' maxW='320px'>
        <Text
          flex='1'
          fontSize='xs'
          color={categoryId == null ? (isInvalid ? 'red.500' : 'gray.400') : 'gray.600'}
          whiteSpace='normal'
          lineHeight='short'>
          {categoryId == null ? 'No category' : breadcrumb}
        </Text>
        <GridCategoryModal
          value={categoryId != null ? { id: categoryId, title: product.categoryTitle } : null}
          onSelect={onSelect}
          trigger={
            <IconButtonWithTooltip
              size='xs'
              variant='ghost'
              label='Edit category'
              aria-label='edit-category-btn'
              Icon={<Icon as={FiEdit} />}
            />
          }
        />
      </HStack>
    </Td>
  );
};
